// =====================================================
// src/services/Http.js
// -----------------------------------------------------
// Cliente HTTP centralizado para el frontend
//
// Funciones principales:
// - Adjuntar access_token automáticamente
// - Renovar access_token usando refresh_token
// - Refresh proactivo (antes de expirar)
// - Refresh reactivo (cuando backend responde 401)
// - Logout automático si refresh_token es inválido
// =====================================================

import axios from 'axios';

// =====================================================
// URL del backend (definida en .env del frontend)
// REACT_APP_SERVER_URL=http://IP:PUERTO
// =====================================================
export const SERVER_URL = process.env.REACT_APP_SERVER_URL;

// =====================================================
// Cliente HTTP principal (usado por todos los services)
// =====================================================
const http = axios.create({
  baseURL: SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =====================================================
// Cliente HTTP exclusivo para refresh
// ❗ NO tiene interceptores (evita loops infinitos)
// =====================================================
const refreshClient = axios.create({
  baseURL: SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

const shouldBypassAuthRefresh = (config = {}) => {
  const url = String(config?.url || '');
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/me/logout')
  );
};

// - Refresh reactivo (cuando backend responde 401)
const refreshTokens = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const rawRefreshToken = localStorage.getItem('refresh_token');
    if (!rawRefreshToken) {
      throw new Error('No existe refresh_token');
    }

    const response = await refreshClient.post(
      '/auth/refresh',
      {
        refresh_token: rawRefreshToken.trim(),
      }
    );


    const {
      access_token,
      refresh_token: newRefreshToken,
    } = response.data;

    localStorage.setItem('access_token', access_token);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }

    return access_token;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};


// =====================================================
// INTERCEPTOR DE REQUEST
//
// - Adjunta access_token
// - Refresca proactivamente si está por expirar
// =====================================================
http.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('access_token');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// =====================================================
// INTERCEPTOR DE RESPONSE
//
// - Maneja expiración real del token (401)
// - Reintenta UNA sola vez
// =====================================================
http.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !shouldBypassAuthRefresh(originalRequest)
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshTokens();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return http(originalRequest);
      } catch (refreshError) {
        localStorage.clear();
        if (window.location.pathname !== '/') {
          window.location.href = (process.env.PUBLIC_URL || '') + '/';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);


// =====================================================
// Exportar cliente HTTP
// =====================================================
export default http;
